import React, { ReactNode, useEffect, useState, useRef } from 'react';
import classNames from 'classnames';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Icons,
  Button,
  ToolButton,
} from '../';
import { IconPresentationProvider } from '@ohif/ui-next';
import { Mail, BadgeCheck } from 'lucide-react';
import NavBar from '../NavBar';
import './Header.css';

interface HeaderProps {
  children?: ReactNode;
  menuOptions: Array<{
    title: string;
    icon?: string;
    onClick: () => void;
  }>;
  isReturnEnabled?: boolean;
  onClickReturnButton?: () => void;
  returnLink?: string;
  isSticky?: boolean;
  WhiteLabeling?: {
    createLogoComponentFn?: (React: any, props: any) => ReactNode;
  };
  PatientInfo?: ReactNode;
  Secondary?: ReactNode;
  UndoRedo?: ReactNode;
}

type UserClaims = {
  name?: string | null;
  preferred_username?: string | null;
  uid?: string | null;
};

function decodeJwt(token: string | null): Record<string, any> | null {
  if (!token) return null;

  try {
    const parts = token.split('.');

    if (parts.length < 2) return null;

    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');

    const pad = base64.length % 4;

    const padded = pad ? base64 + '='.repeat(4 - pad) : base64;

    return JSON.parse(atob(padded));
  } catch (e) {
    console.error('decodeJwt error', e);
    return null;
  }
}

function getOidcUserFromSession():
  | (UserClaims & {
      access_token?: string;
      id_token?: string;
    })
  | null {
  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);

      if (!key) continue;

      const val = sessionStorage.getItem(key) || '';

      try {
        const parsed = JSON.parse(val);

        if (parsed?.access_token || parsed?.id_token) {
          const token = parsed.access_token || parsed.id_token;

          const claims = decodeJwt(token);

          if (claims) {
            return {
              name: claims.name || null,
              preferred_username: claims.preferred_username || null,
              uid: claims.uid || claims.sub || null,
              access_token: parsed.access_token,
              id_token: parsed.id_token,
            };
          }
        }
      } catch {
        // Ignore invalid sessionStorage entries
      }
    }
  } catch (err) {
    console.error('getOidcUserFromSession error', err);
  }

  return null;
}

function Header({
  children,
  menuOptions,
  isReturnEnabled = true,
  onClickReturnButton,
  returnLink,
  isSticky = false,
  WhiteLabeling,
  PatientInfo,
  UndoRedo,
  Secondary,
  ...props
}: HeaderProps): JSX.Element {
  const [claims, setClaims] = useState<UserClaims | null>(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const user = getOidcUserFromSession();

    if (user) {
      setClaims(user);
    }
  }, []);

  /*
   * Close profile menu when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  /*
   * User name / initials
   */
  const rawName = claims?.name || claims?.preferred_username || null;

  const nameParts = rawName ? String(rawName).trim().split(/\s+/) : [];

  const firstWord = nameParts[0] || 'Guest';

  let initials = firstWord[0]?.toUpperCase() || 'G';

  if (nameParts.length > 1) {
    initials += nameParts[1][0]?.toUpperCase();
  }

  /*
   * Return / Back button
   */
  const onClickReturn = () => {
    if (!isReturnEnabled) return;

    if (returnLink) {
      window.history.pushState({}, '', returnLink);

      window.dispatchEvent(new PopStateEvent('popstate'));
    } else if (onClickReturnButton) {
      onClickReturnButton();
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(prev => !prev);
  };

  return (
    <IconPresentationProvider
      size="large"
      IconContainer={ToolButton}
    >
      <div className="lilly-header-wrapper">
        <NavBar
          isSticky={isSticky}
          {...props}
        >
          <div className="lilly-header">
            {/* -------------------------------- */}
            {/* LEFT SIDE */}
            {/* -------------------------------- */}

            <div className="lilly-header-left">
              <div
                className={classNames('lilly-brand-area', isReturnEnabled && 'lilly-clickable')}
                onClick={onClickReturn}
                data-cy="return-to-work-list"
              >
                {isReturnEnabled && <Icons.ArrowLeft className="lilly-back-icon" />}

                <div className="lilly-logo">
                  {WhiteLabeling?.createLogoComponentFn?.(React, props) || <Icons.OHIFLogo />}
                </div>
              </div>
            </div>

            {/* -------------------------------- */}
            {/* SECONDARY */}
            {/* -------------------------------- */}

            {Secondary && <div className="lilly-secondary">{Secondary}</div>}

            {/* -------------------------------- */}
            {/* CENTER */}
            {/* -------------------------------- */}

            <div className="lilly-header-center">
              <div className="lilly-center-content">{children}</div>
            </div>

            {/* -------------------------------- */}
            {/* RIGHT SIDE */}
            {/* -------------------------------- */}

            <div className="lilly-header-right">
              {UndoRedo && <div className="lilly-control">{UndoRedo}</div>}

              {PatientInfo && <div className="lilly-control">{PatientInfo}</div>}

              {/* Profile */}
              <div
                ref={menuRef}
                className="lilly-profile"
                onClick={toggleMenu}
                title={rawName ?? 'Guest'}
              >
                <div className="lilly-avatar">{initials}</div>

                <div className="lilly-profile-name">{firstWord}</div>

                <Icons.ChevronDown className="lilly-chevron" />

                {isMenuOpen && (
                  <div
                    className="lilly-profile-menu"
                    onClick={e => e.stopPropagation()}
                  >
                    {claims ? (
                      <>
                        <div className="lilly-profile-header">
                          <div className="lilly-profile-title">{claims.name || firstWord}</div>
                        </div>

                        <div className="lilly-profile-content">
                          <div className="lilly-profile-row">
                            <Mail className="lilly-profile-icon lilly-blue" />

                            <div>
                              <span className="lilly-profile-label">Username</span>

                              <span className="lilly-profile-value">
                                {claims.preferred_username || '-'}
                              </span>
                            </div>
                          </div>

                          <div className="lilly-profile-row">
                            <BadgeCheck className="lilly-profile-icon lilly-green" />

                            <div>
                              <span className="lilly-profile-label">SYSTEM ID</span>

                              <span className="lilly-profile-value">{claims.uid || '-'}</span>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="lilly-guest">Guest User</div>
                    )}
                  </div>
                )}
              </div>

              {/* Settings */}
              {menuOptions && menuOptions.length > 0 && (
                <div className="lilly-settings">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="lilly-settings-button"
                      >
                        <Icons.GearSettings />
                      </Button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent align="end">
                      {menuOptions.map((option, index) => {
                        const IconComponent = option.icon
                          ? (Icons[option.icon as keyof typeof Icons] as any)
                          : null;

                        return (
                          <DropdownMenuItem
                            key={index}
                            onSelect={option.onClick}
                            className="flex items-center gap-2 py-2"
                          >
                            {IconComponent && (
                              <span className="flex h-4 w-4 items-center justify-center">
                                <Icons.ByName name={IconComponent.name} />
                              </span>
                            )}

                            <span className="flex-1">{option.title}</span>
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </div>
          </div>
        </NavBar>
      </div>
    </IconPresentationProvider>
  );
}

export default Header;

export { getOidcUserFromSession };
